import type { FragmentType } from '~/graphql/generated/fragment-masking';
import type { OverviewLearningOutcomeViewModel, OverviewSkillViewModel } from '~/types/unified';

import { skillFragment } from '~/graphql/documents/award';
import { publicFileFragment } from '~/graphql/documents/core';
import { useFragment } from '~/graphql/generated';

export const parseLearningOutcomes = (raw: string | null | undefined, assetId: string | null = null, iconUrl: string | null = null): OverviewLearningOutcomeViewModel[] => {
  if (!raw || !raw.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item: unknown): item is { label: string; description?: string } =>
          typeof item === 'object' && item !== null && 'label' in item && typeof (item as Record<string, unknown>).label === 'string',
        )
        .map(item => ({
          title: item.label,
          description: item.description ?? null,
          assetId,
          iconUrl,
        }));
    }
  }
  catch {
    // Not JSON — treat as plain text with one outcome per line
  }

  return raw
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => ({ title: line, description: null, assetId, iconUrl }));
};

export const mapSkillsToViewModel = (
  skills: ReadonlyArray<FragmentType<typeof skillFragment>> | null | undefined,
): OverviewSkillViewModel[] => {
  if (!skills?.length) {
    return [];
  }

  const resolved = useFragment(skillFragment, skills);

  return resolved.map((skill) => {
    const iconUrl = skill.iconFile
      ? useFragment(publicFileFragment, skill.iconFile)?.publicUrl ?? null
      : null;

    return {
      id: skill.id,
      name: skill.name,
      slug: skill.slug,
      iconUrl,
    };
  });
};
